import React from 'react';
import useAnalytics from '../../../../hooks/useAnalytics';
import {
	EventAction,
	EventCategory,
	EventKeys,
	EventType,
	EventArgs,
} from '../../../../services/analytics';

export enum UserActionType {
	focus = 'focus',
	click = 'click',
}

export type AnalyticsProps = {
	analytics?: {
		eventType: EventType;
		eventLabel: EventAction;
		eventCategory: EventCategory;
		eventArgs: EventArgs;
		userActionType: UserActionType;
		pageRequestTime?: number;
		eventAction?: string;
	};
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const withAnalytics =
	<T extends AnalyticsProps>(WrappedComponent: React.ComponentType<T>) =>
	({ ...props }: T) => {
		const analytics = useAnalytics();

		const sendAnalytics = (
			eventType: EventType,
			eventLabel: EventAction,
			eventCategory: EventCategory,
			eventAction: string | React.ReactNode,
			eventArgs: EventArgs,
			pageRequestTime?: number
		) => {
			switch (eventType) {
				case EventType.PageRequestTimeEvent:
					analytics.sendEvent({
						[EventKeys.PageRequestTime]: new Date().getTime(),
					});
					break;
				case EventType.SimpleEvent:
					analytics.sendSimpleEvent(
						eventCategory,
						eventAction as string,
						eventLabel,
						pageRequestTime
					);
					break;
				default:
					analytics.sendEvent(eventArgs);
			}
		};

		const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
			if (
				props.analytics &&
				props.analytics.userActionType === UserActionType.click
			) {
				sendAnalytics(
					props.analytics.eventType,
					props.analytics.eventLabel,
					props.analytics.eventCategory,
					props.analytics.eventAction ?? props.children,
					props.analytics.eventArgs,
					props.analytics.pageRequestTime
				);
			}
			if (props.onClick) {
				props.onClick(e);
			}
		};

		const onFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
			if (
				props.analytics &&
				props.analytics.userActionType === UserActionType.focus
			) {
				sendAnalytics(
					props.analytics.eventType,
					props.analytics.eventLabel,
					props.analytics.eventCategory,
					EventAction.FieldFocus,
					props.analytics.eventArgs,
					props.analytics.pageRequestTime
				);
			}
			if (props.onFocus) {
				props.onFocus(e);
			}
		};

		return (
			<WrappedComponent {...(props as T)} onClick={onClick} onFocus={onFocus} />
		);
	};

export default withAnalytics;
